import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'components/ui/Header';
import HeroSection from './components/HeroSection';
import VisionStatement from './components/VisionStatement';
import TransformationPrinciples from './components/TransformationPrinciples';
import FutureLeadership from './components/FutureLeadership';
import CTASection from './components/CTASection';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const OurVisionPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen" style={{
      background: `linear-gradient(135deg, 
        rgba(249, 250, 251, 1) 0%, 
        rgba(255, 255, 255, 1) 30%, 
        rgba(217, 59, 52, 0.05) 60%, 
        rgba(155, 142, 86, 0.05) 100%)`
    }}>
      <Header />
      {/* Enhanced Hero Section */}
      <HeroSection />
      {/* Vision Statement Enhanced with Brand Colors */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: `linear-gradient(90deg, 
            rgba(217, 59, 52, 0.05) 0%, 
            transparent 50%, 
            rgba(255, 215, 0, 0.05) 100%)`
        }}></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 gap-16 items-center">
            {/* Vision Statement */}
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-3 px-6 py-3 rounded-full font-bold border" style={{
                  background: `linear-gradient(135deg, rgba(217, 59, 52, 0.1) 0%, rgba(155, 142, 86, 0.1) 100%)`,
                  color: '#D93B34',
                  borderColor: 'rgba(217, 59, 52, 0.3)'
                }}>
                  <Icon name="Target" size={20} />
                  <span>Notre Mission Fondamentale</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                  Forts de notre héritage, nous aspirons à 
                  <span className="text-transparent bg-clip-text block" style={{
                    background: `linear-gradient(135deg, #D93B34 0%, #9B8E56 50%, #FFD700 100%)`,
                    WebkitBackgroundClip: 'text'
                  }}> l'excellence</span>
                </h2>
                <div className="prose prose-xl text-gray-700 leading-relaxed">
                  <p className="text-xl">
                    Forts de notre héritage, nous aspirons à devenir le <strong style={{color: '#D93B34'}}>partenaire stratégique de référence</strong> pour les entreprises en quête d'excellence. Notre mission est de transformer les défis de nos clients en opportunités de croissance durable en exploitant la puissance du savoir technologique, scientifique et de l'Intelligence Artificielle.
                  </p>
                </div>
              </div>

              {/* Enhanced Mission Statement */}
              <div className="border-l-8 p-10 rounded-r-3xl shadow-lg" style={{
                background: `linear-gradient(135deg, 
                  rgba(217, 59, 52, 0.05) 0%, 
                  rgba(155, 142, 86, 0.05) 50%, 
                  rgba(255, 215, 0, 0.05) 100%)`,
                borderLeftColor: '#D93B34'
              }}>
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg" style={{
                    background: `linear-gradient(135deg, #D93B34 0%, #9B8E56 100%)`
                  }}>
                    <Icon name="Target" size={32} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Notre Mission Transformationnelle</h3>
                    <p className="text-gray-700 leading-relaxed text-lg">
                      Transformer les défis de nos clients en <span className="font-bold" style={{color: '#D93B34'}}>opportunités de croissance durable</span> en exploitant la puissance du savoir technologique, scientifique et de l'Intelligence Artificielle pour créer un avantage concurrentiel durable.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Button Enhanced */}
              <div className="pt-8">
                <button className="inline-flex items-center space-x-4 text-white px-10 py-5 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 border" style={{
                  background: `linear-gradient(135deg, #D93B34 0%, #9B8E56 50%, #D93B34 100%)`,
                  borderColor: 'rgba(217, 59, 52, 0.6)'
                }}>
                  <Icon name="ArrowRight" size={24} />
                  <span>Découvrir Notre Approche Stratégique</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Enhanced Expertise Domains with Brand Colors */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{
        background: `linear-gradient(135deg, 
          rgba(249, 250, 251, 1) 0%, 
          rgba(217, 59, 52, 0.03) 50%, 
          rgba(155, 142, 86, 0.03) 100%)`
      }}>
        <div className="absolute inset-0" style={{
          background: `linear-gradient(90deg, 
            rgba(217, 59, 52, 0.05) 0%, 
            transparent 50%, 
            rgba(255, 215, 0, 0.05) 100%)`
        }}></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-20">
            <div className="inline-flex items-center space-x-3 px-6 py-3 rounded-full font-bold mb-8 border" style={{
              background: `linear-gradient(135deg, rgba(217, 59, 52, 0.1) 0%, rgba(155, 142, 86, 0.1) 100%)`,
              color: '#D93B34',
              borderColor: 'rgba(217, 59, 52, 0.3)'
            }}>
              <Icon name="Star" size={20} />
              <span>Nos Domaines d'Excellence</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">Expertise & Excellence Continue</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Nous continuons de servir nos clients dans des domaines clés, toujours animés par l'excellence en conseil, formation et études.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Gestion des Ressources Humaines",
                icon: "Users",
                color: `linear-gradient(135deg, #D93B34 0%, #9B8E56 100%)`,
                borderColor: 'rgba(217, 59, 52, 0.3)',
                bgColor: `linear-gradient(135deg, rgba(217, 59, 52, 0.05) 0%, rgba(155, 142, 86, 0.05) 100%)`,
                description: "Digitalisation et optimisation des processus RH avec IA intégrée",
                link: "/human-resources-management-services"
              },
              {
                title: "Marketing Digital",
                icon: "Megaphone",
                color: `linear-gradient(135deg, #9B8E56 0%, #FFD700 100%)`,
                borderColor: 'rgba(155, 142, 86, 0.3)',
                bgColor: `linear-gradient(135deg, rgba(155, 142, 86, 0.05) 0%, rgba(255, 215, 0, 0.05) 100%)`,
                description: "Stratégies numériques et transformation digitale innovante",
                link: "/digital-marketing-services"
              },
              {
                title: "Management de Projets Numériques",
                icon: "FolderOpen",
                color: `linear-gradient(135deg, #FFD700 0%, #D93B34 100%)`,
                borderColor: 'rgba(255, 215, 0, 0.3)',
                bgColor: `linear-gradient(135deg, rgba(255, 215, 0, 0.05) 0%, rgba(217, 59, 52, 0.05) 100%)`,
                description: "Pilotage agile et excellence opérationnelle",
                link: "/digital-project-management-services"
              },
              {
                title: "Intelligence Artificielle",
                icon: "Brain",
                color: `linear-gradient(135deg, #D93B34 0%, #FFD700 100%)`,
                borderColor: 'rgba(217, 59, 52, 0.3)',
                bgColor: `linear-gradient(135deg, rgba(217, 59, 52, 0.05) 0%, rgba(255, 215, 0, 0.05) 100%)`,
                description: "Innovation technologique et solutions IA éthiques",
                link: "/ai-solutions-integration-hub"
              }
            ]?.map((domain, index) => (
              <div key={index} className="group">
                <div className="rounded-3xl p-10 shadow-lg hover:shadow-2xl transform hover:-translate-y-3 transition-all duration-500 border-2 hover:border-opacity-100" style={{
                  background: domain?.bgColor,
                  borderColor: domain?.borderColor
                }}>
                  <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-lg" style={{
                    background: domain?.color
                  }}>
                    <Icon name={domain?.icon} size={40} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-6 leading-tight">{domain?.title}</h3>
                  <p className="text-gray-700 leading-relaxed mb-8">{domain?.description}</p>
                  
                  <div className="pt-6 border-t border-gray-200">
                    <button 
                      onClick={() => domain?.link && navigate(domain?.link)}
                      className="inline-flex items-center space-x-2 font-bold transition-colors duration-200 group-hover:translate-x-2" 
                      style={{
                        color: '#4B5563'
                      }} 
                      onMouseEnter={(e) => e.target.style.color = '#D93B34'} 
                      onMouseLeave={(e) => e.target.style.color = '#4B5563'}
                    >
                      <span>En savoir plus</span>
                      <Icon name="ArrowRight" size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Enhanced Excellence Philosophy with Brand Colors */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: `linear-gradient(90deg, 
            rgba(217, 59, 52, 0.03) 0%, 
            transparent 50%, 
            rgba(155, 142, 86, 0.03) 100%)`
        }}></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="rounded-[3rem] p-2 shadow-2xl" style={{
            background: `linear-gradient(135deg, #D93B34 0%, #9B8E56 50%, #FFD700 100%)`
          }}>
            <div className="bg-white rounded-[3rem] p-16 relative overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-center">
                <div className="lg:col-span-2 space-y-10">
                  <div className="space-y-8">
                    <div className="inline-flex items-center space-x-4 px-8 py-4 rounded-full font-bold border" style={{
                      background: `linear-gradient(135deg, rgba(217, 59, 52, 0.1) 0%, rgba(155, 142, 86, 0.1) 100%)`,
                      color: '#D93B34',
                      borderColor: 'rgba(217, 59, 52, 0.3)'
                    }}>
                      <Icon name="Award" size={24} />
                      <span>Excellence Continue & Innovation</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                      Toujours animés par l'excellence en 
                      <span className="text-transparent bg-clip-text block mt-2" style={{
                        background: `linear-gradient(135deg, #D93B34 0%, #9B8E56 50%, #FFD700 100%)`,
                        WebkitBackgroundClip: 'text'
                      }}>
                        conseil, formation et études
                      </span>
                    </h2>
                    <p className="text-xl text-gray-700 leading-relaxed">
                      Nous intégrons les technologies émergentes et l'Intelligence Artificielle pour créer un avantage concurrentiel durable et accompagner nos clients vers la transformation digitale avec une approche éthique et responsable.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                      { 
                        title: "Conseil", 
                        icon: "MessageSquare", 
                        desc: "Expertise stratégique & conseil en transformation",
                        color: `linear-gradient(135deg, #D93B34 0%, #9B8E56 100%)`
                      },
                      { 
                        title: "Formation", 
                        icon: "GraduationCap", 
                        desc: "Développement des compétences & montée en expertise",
                        color: `linear-gradient(135deg, #9B8E56 0%, #FFD700 100%)`
                      },
                      { 
                        title: "Études", 
                        icon: "Search", 
                        desc: "Recherche, analyse & veille technologique",
                        color: `linear-gradient(135deg, #FFD700 0%, #D93B34 100%)`
                      }
                    ]?.map((pillar, index) => (
                      <div key={index} className="text-center space-y-6 bg-gray-50 p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-all duration-300">
                        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-lg" style={{
                          background: pillar?.color
                        }}>
                          <Icon name={pillar?.icon} size={36} className="text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{pillar?.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{pillar?.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <div className="rounded-3xl p-12 h-[400px] flex items-center justify-center relative overflow-hidden shadow-xl" style={{
                    background: `linear-gradient(135deg, 
                      rgba(217, 59, 52, 0.1) 0%, 
                      rgba(155, 142, 86, 0.1) 50%, 
                      rgba(255, 215, 0, 0.1) 100%)`
                  }}>
                    <Icon name="Award" size={150} className="relative z-10" style={{color: '#D93B34', opacity: 0.8}} />
                    <div className="absolute inset-0 rounded-3xl" style={{
                      background: `linear-gradient(to top, rgba(217, 59, 52, 0.1) 0%, transparent 100%)`
                    }}></div>
                    <div className="absolute top-6 right-6 w-20 h-20 rounded-full flex items-center justify-center shadow-lg animate-pulse" style={{
                      background: `linear-gradient(135deg, #FFD700 0%, #D93B34 100%)`
                    }}>
                      <Icon name="Star" size={32} className="text-white" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Enhanced Decorative Elements with Brand Colors */}
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full opacity-60 animate-pulse" style={{
                background: `linear-gradient(135deg, rgba(217, 59, 52, 0.2) 0%, rgba(155, 142, 86, 0.2) 100%)`
              }}></div>
              <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full opacity-40 animate-pulse" style={{
                background: `linear-gradient(135deg, rgba(155, 142, 86, 0.2) 0%, rgba(255, 215, 0, 0.2) 100%)`,
                animationDelay: '1s'
              }}></div>
            </div>
          </div>
        </div>
      </section>
      {/* Enhanced Vision Components */}
      <VisionStatement />
      <TransformationPrinciples />
      <FutureLeadership />
      {/* Enhanced CTA Section */}
      <CTASection />
      {/* Enhanced Footer with Brand Colors and Transparency */}
      <footer className="text-white py-20 relative overflow-hidden" style={{
        background: `linear-gradient(135deg, 
          rgba(31, 41, 55, 0.95) 0%, 
          rgba(17, 24, 39, 0.95) 50%, 
          rgba(0, 0, 0, 0.95) 100%)`
      }}>
        <div className="absolute inset-0" style={{
          background: `linear-gradient(90deg, 
            rgba(217, 59, 52, 0.1) 0%, 
            transparent 50%, 
            rgba(255, 215, 0, 0.1) 100%)`
        }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info Enhanced */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <Image
                  src="/assets/images/logo_CRF_PERFECTION_4x-1761998753526.png"
                  alt="CRF PERFECTION logo featuring interlocking heart-shaped design representing transformational excellence and strategic partnership vision with professional consulting excellence"
                  className="w-16 h-16 object-contain"
                />
                <div>
                  <h3 className="text-2xl font-bold">CRF PERFECTION</h3>
                  <p className="text-gray-400">Partenaire Stratégique de Référence</p>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Transformons ensemble vos défis en opportunités de croissance durable grâce à l'excellence, l'innovation et l'Intelligence Artificielle éthique.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                  background: `linear-gradient(135deg, #D93B34 0%, #9B8E56 100%)`
                }}>
                  <Icon name="Linkedin" size={20} className="text-white" />
                </div>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{
                  background: `linear-gradient(135deg, #9B8E56 0%, #FFD700 100%)`
                }}>
                  <Icon name="Twitter" size={20} className="text-white" />
                </div>
              </div>
            </div>

            {/* Enhanced Vision Links */}
            <div className="space-y-6">
              <h4 className="text-xl font-bold text-white border-l-4 pl-4" style={{borderLeftColor: '#D93B34'}}>Notre Vision</h4>
              <ul className="space-y-3">
                <li><a href="#mission" className="text-gray-400 transition-colors duration-200 flex items-center space-x-2 hover:text-yellow-400"><Icon name="ArrowRight" size={14} /><span>Mission & Valeurs</span></a></li>
                <li><a href="#expertise" className="text-gray-400 transition-colors duration-200 flex items-center space-x-2 hover:text-yellow-400"><Icon name="ArrowRight" size={14} /><span>Domaines d'Expertise</span></a></li>
                <li><a href="#innovation" className="text-gray-400 transition-colors duration-200 flex items-center space-x-2 hover:text-yellow-400"><Icon name="ArrowRight" size={14} /><span>Innovation & IA</span></a></li>
                <li><a href="#excellence" className="text-gray-400 transition-colors duration-200 flex items-center space-x-2 hover:text-yellow-400"><Icon name="ArrowRight" size={14} /><span>Excellence Continue</span></a></li>
              </ul>
            </div>

            {/* Enhanced Services */}
            <div className="space-y-6">
              <h4 className="text-xl font-bold text-white border-l-4 pl-4" style={{borderLeftColor: '#9B8E56'}}>Nos Services</h4>
              <ul className="space-y-3">
                <li><a href="/human-resources-management-services" className="text-gray-400 transition-colors duration-200 flex items-center space-x-2 hover:text-yellow-400"><Icon name="ArrowRight" size={14} /><span>Ressources Humaines</span></a></li>
                <li><a href="/digital-marketing-services" className="text-gray-400 transition-colors duration-200 flex items-center space-x-2 hover:text-yellow-400"><Icon name="ArrowRight" size={14} /><span>Marketing Digital</span></a></li>
                <li><a href="/digital-project-management-services" className="text-gray-400 transition-colors duration-200 flex items-center space-x-2 hover:text-yellow-400"><Icon name="ArrowRight" size={14} /><span>Projets Numériques</span></a></li>
                <li><a href="/ai-solutions-integration-hub" className="text-gray-400 transition-colors duration-200 flex items-center space-x-2 hover:text-yellow-400"><Icon name="ArrowRight" size={14} /><span>Intelligence Artificielle</span></a></li>
              </ul>
            </div>

            {/* Enhanced Contact */}
            <div className="space-y-6">
              <h4 className="text-xl font-bold text-white border-l-4 pl-4" style={{borderLeftColor: '#FFD700'}}>Contact</h4>
              <div className="space-y-4">
                <a href="/consultation" className="flex items-center space-x-3 text-gray-400 transition-colors duration-200 p-3 rounded-lg hover:text-yellow-400" style={{backgroundColor: 'rgba(75, 85, 99, 0.3)'}}>
                  <Icon name="MessageSquare" size={20} />
                  <span>Consultation Gratuite</span>
                </a>
                <a href="tel:+229" className="flex items-center space-x-3 text-gray-400 transition-colors duration-200 p-3 rounded-lg hover:text-yellow-400" style={{backgroundColor: 'rgba(75, 85, 99, 0.3)'}}>
                  <Icon name="Phone" size={20} />
                  <span>Cotonou, Bénin</span>
                </a>
                <a href="mailto:contact@crfperfection.com" className="flex items-center space-x-3 text-gray-400 transition-colors duration-200 p-3 rounded-lg hover:text-yellow-400" style={{backgroundColor: 'rgba(75, 85, 99, 0.3)'}}>
                  <Icon name="Mail" size={20} />
                  <span>contact@crfperfection.com</span>
                </a>
              </div>
            </div>
          </div>

          {/* Enhanced Bottom Footer */}
          <div className="border-t mt-16 pt-12" style={{borderTopColor: 'rgba(75, 85, 99, 0.5)'}}>
            <div className="flex flex-col md:flex-row items-center justify-between">
              <p className="text-gray-500 mb-4 md:mb-0 text-lg">
                © 2024 CRF Perfection - Notre Vision. <span style={{color: '#FFD700'}} className="font-semibold">Transformons l'avenir ensemble.</span>
              </p>
              <div className="flex space-x-8">
                <a href="/privacy" className="text-gray-500 hover:text-white transition-colors duration-200 flex items-center space-x-2">
                  <Icon name="Shield" size={16} />
                  <span>Confidentialité</span>
                </a>
                <a href="/terms" className="text-gray-500 hover:text-white transition-colors duration-200 flex items-center space-x-2">
                  <Icon name="FileText" size={16} />
                  <span>Conditions</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OurVisionPage;